import { Badge, Grid } from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import { useScrollButtonState } from '@ncr-retail/hooks';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CheckMark } from 'src/common/media/Checkmark.svg';
import Pagination from 'src/pagination/pagination';
import VerticalCenter from 'src/vertical-center/vertical-center';
import BaseButton from '../base-button';
import { styles } from './multi-select-list-style';

export const MultiSelectList: React.FC<Props> = ({
  data,
  title,
  message,
  subMessage,
  acceptLabel,
  onClose,
  onConfirm,
}): JSX.Element => {
  const classes = styles();
  const [selectedList, setSelectedList] = useState<ListItemData[]>([]);
  const [allSelected, setAllSelected] = useState<boolean>(false);
  const { t } = useTranslation();
  const [hasPreSelectedOptions, setHasPreSelectedOptions] = useState<boolean>(false);
  const [{ currentPage, lastPage, onPageChanged }, scrollRef] = useScrollButtonState();

  const listItemClickHandler = (id: string): void => {
    const updatedList = selectedList.map(listItem => {
      if (listItem.id === id) {
        listItem.selected = !listItem.selected;
        return listItem;
      }
      return listItem;
    });
    setSelectedList(updatedList);
  };

  const allItemsClickHandler = (): void => {
    selectedList.map(listItem => {
      listItem.selected = !allSelected;
      return listItem;
    });
    setAllSelected(!allSelected);
  };

  useEffect(() => {
    const isAllSelected =
      selectedList.length === selectedList.filter(listItem => listItem.selected).length;
    setAllSelected(isAllSelected);
  }, [selectedList]);

  useEffect(() => {
    setSelectedList(data);
    if (data.filter(option => option.selected).length) {
      setHasPreSelectedOptions(true);
    }
  }, [data]);

  useEffect(() => {
    return () => setSelectedList([]);
  }, []);

  return (
    <Grid container direction='column' justifyContent='space-between'>
      <Grid
        className={classes.header}
        container
        direction='row'
        justifyContent='space-between'
        alignItems='center'
      >
        <Grid item>
          <span className={classes.spacing}>{title ?? ''}</span>
        </Grid>
        <Grid item>
          <HelpIcon classes={{ root: `${classes.icon} ${classes.decision}` }} />
        </Grid>
      </Grid>

      <Grid item className={`${classes.displayBody} fullWidth`}>
        <Grid container direction='column' className='fullHeight'>
          <Grid item className={classes.messageWrapper}>
            <Grid
              container
              className='fullHeight'
              direction='row'
              justifyContent='center'
              alignItems='center'
            >
              <Grid className={classes.message} data-testid='message'>
                {message}
              </Grid>
              <Grid className={classes.subMessage} data-testid='sub-message'>
                {subMessage}
              </Grid>
            </Grid>
          </Grid>
          <Grid container className={classes.list}>
            <ListItem
              id='all'
              name={t('All')}
              selected={allSelected}
              clickHandler={() => allItemsClickHandler()}
            />
          </Grid>
          <Grid item className={`fullWidth ${classes.listContainer}`}>
            <div ref={scrollRef} className='verticalScroll fullWidth fullHeight'>
              <Grid container className={classes.list}>
                {selectedList.map(
                  (item): JSX.Element => {
                    return (
                      <ListItem
                        id={item.id}
                        name={item.name}
                        selected={item.selected}
                        key={item.id}
                        clickHandler={() => listItemClickHandler(item.id)}
                      />
                    );
                  },
                )}
              </Grid>
            </div>
          </Grid>
          <Grid item className={classes.selectListBottomContainer}>
            <Grid container alignContent='flex-end' className='fullHeight'>
              <Grid item xs={3}>
                <Grid container justifyContent='flex-start'>
                  <Grid item>
                    <VerticalCenter>
                      <Grid container justifyContent='center' alignContent='center'>
                        <BaseButton
                          buttonType='white'
                          data-testid='select-list-close-button'
                          color='default'
                          className={classes.closeButton}
                          onClick={onClose}
                        >
                          <span className='label'>{t('ButtonCancelStyle_Cancel')}</span>
                        </BaseButton>
                      </Grid>
                    </VerticalCenter>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <Grid container>
                  {lastPage > 1 && (
                    <Grid container justifyContent='center' className='fullHeight'>
                      <Pagination
                        currentPage={currentPage}
                        totalPages={lastPage}
                        updateCurrentPageHandler={onPageChanged}
                        testIdPrefix={'multi-select-list'}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={3}>
                <Grid container justifyContent='flex-end'>
                  <Grid item>
                    <VerticalCenter>
                      <Grid container justifyContent='center' alignContent='center'>
                        <BaseButton
                          buttonType='primary'
                          data-testid='select-list-accept-button'
                          className={classes.confirmButton}
                          onClick={() =>
                            onConfirm(
                              selectedList.filter((listItem: ListItemData) => listItem.selected),
                            )
                          }
                          disabled={
                            selectedList.filter(listItem => listItem.selected).length === 0 &&
                            !hasPreSelectedOptions
                          }
                        >
                          <span className='label'>{acceptLabel ?? t('Confirm')}</span>
                        </BaseButton>
                      </Grid>
                    </VerticalCenter>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const ListItem: React.FC<SelectListItem> = ({
  id,
  name,
  description,
  selected,
  clickHandler,
}: SelectListItem): JSX.Element => {
  const classes = styles();
  return (
    <Grid item key={id} data-testid={`select-${id.toLowerCase()}-container`}>
      <Badge
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        showZero={true}
        invisible={!selected}
        badgeContent={<CheckMark />}
        classes={{ badge: classes.badge }}
      >
        {' '}
      </Badge>
      <Grid
        item
        data-testid={`select-${id.toLowerCase()}`}
        className={`${classes.listItem} ${selected ? `${classes.selected}` : ''}`}
        onClick={(): void => clickHandler()}
      >
        <Grid
          container
          className={`fullHeight`}
          justifyContent='space-between'
          alignContent='center'
        >
          <Grid item>{name || description}</Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export interface SelectListItem {
  id: string;
  clickHandler: () => void;
  key?: string;
  name?: string;
  description?: string;
  selected?: boolean;
}

export interface ListItemData {
  id: string;
  name?: string;
  description?: string;
  selected?: boolean;
}

interface Props {
  data: ListItemData[];
  title?: string;
  message?: string;
  subMessage?: string;
  acceptLabel?: string;
  onClose: () => void;
  onConfirm: (id: ListItemData[]) => void;
}
